import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  FEEDBACK_CATEGORY_LABELS,
  type FeedbackCategory,
  type FeedbackSide,
  type SubmitFeedbackBody,
  lookupTerminologyByText,
  submitFeedback,
} from "@/api/feedback";
import { useToast } from "@/components/toast";

interface FeedbackPopoverProps {
  draftId: string;
  selection: {
    text: string;
    side: FeedbackSide;
    offsetStart: number | null;
    offsetEnd: number | null;
    contextBefore: string | null;
    contextAfter: string | null;
    nearestLinkId: string | null;
    rect: DOMRect;
  };
  pairedText?: string | null;
  targetLang: "ru" | "en";
  onClose: () => void;
}

/** Plannotator-style попап для отправки feedback'а к выделенному фрагменту. */
export function FeedbackPopover({
  draftId,
  selection,
  pairedText,
  targetLang,
  onClose,
}: FeedbackPopoverProps) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [categories, setCategories] = useState<Set<FeedbackCategory>>(
    new Set(),
  );
  const [comment, setComment] = useState("");

  // Async lookup похожих терминов в словаре. Ленивый: грузим только если
  // в выделении нет linkId (т.е. фрагмент ещё не привязан к терму).
  const lookupQuery = useQuery({
    queryKey: ["feedback-lookup", selection.text, selection.side, draftId],
    queryFn: async () => {
      const lookupLang: "ru" | "en" =
        selection.side === "source"
          ? targetLang === "ru"
            ? "en"
            : "ru"
          : targetLang;
      const r = await lookupTerminologyByText({
        text: selection.text,
        target_lang: lookupLang,
        draft_id: draftId,
        limit: 3,
      });
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
    staleTime: 60_000,
    enabled: selection.text.length >= 3 && selection.text.length <= 120,
  });

  const submit = useMutation({
    mutationFn: async () => {
      const useConstraint =
        selection.nearestLinkId &&
        selection.nearestLinkId.startsWith("c-");
      const body: SubmitFeedbackBody = {
        side: selection.side,
        anchor_kind: useConstraint ? "constraint" : "offset",
        constraint_id: useConstraint
          ? selection.nearestLinkId!.replace(/^c-/, "")
          : null,
        offset_start: useConstraint ? null : selection.offsetStart,
        offset_end: useConstraint ? null : selection.offsetEnd,
        selected_text: selection.text,
        context_before: selection.contextBefore,
        context_after: selection.contextAfter,
        paired_text: pairedText ?? null,
        categories: Array.from(categories),
        comment_text: comment.trim() || null,
      };
      // anchor_kind=offset требует offsetы; если нет — fallback к
      // вариант, где мы передаём 0/length(text) от начала selected_text
      if (
        body.anchor_kind === "offset" &&
        (body.offset_start == null || body.offset_end == null)
      ) {
        body.offset_start = 0;
        body.offset_end = selection.text.length;
      }
      const r = await submitFeedback(draftId, body);
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
    onSuccess: () => {
      toast("Замечание отправлено", "success");
      void qc.invalidateQueries({
        queryKey: ["draft-feedback", draftId],
      });
      onClose();
    },
    onError: (err: Error) => toast(`Не отправилось: ${err.message}`, "error"),
  });

  // Закрытие по Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Позиционирование: под выделением, со сдвигом если уходит за viewport
  const POPOVER_W = 360;
  const top = Math.min(
    selection.rect.bottom + window.scrollY + 6,
    window.scrollY + window.innerHeight - 360,
  );
  const left = Math.min(
    selection.rect.left + window.scrollX,
    window.scrollX + window.innerWidth - POPOVER_W - 16,
  );

  function toggleCategory(cat: FeedbackCategory) {
    setCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  }

  const truncated =
    selection.text.length > 80
      ? selection.text.slice(0, 78) + "…"
      : selection.text;

  return (
    <div
      className="mt-feedback-popover"
      style={{
        position: "absolute",
        top,
        left,
        width: POPOVER_W,
        zIndex: 1000,
        background: "var(--panel)",
        border: "1px solid var(--border-default, #d0d5dd)",
        borderRadius: 8,
        boxShadow: "0 4px 14px rgba(0,0,0,0.12)",
        padding: 12,
      }}
      onMouseDown={(e) => {
        // не дать selectionchange сбрасывать popover при клике в форму
        e.stopPropagation();
      }}
    >
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          marginBottom: 6,
        }}
      >
        <strong style={{ fontSize: "var(--fs-sm, 13px)" }}>
          Замечание к фрагменту
        </strong>
        <span
          className="mt-badge mt-badge--neutral mt-badge--no-dot"
          style={{ fontSize: "10px" }}
        >
          {selection.side === "source" ? "оригинал" : "перевод"}
        </span>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          className="mt-icon-btn"
          onClick={onClose}
          title="Закрыть (Esc)"
          style={{ padding: "0 6px" }}
        >
          ✕
        </button>
      </div>

      <div
        style={{
          fontSize: "var(--fs-sm, 13px)",
          color: "var(--text-soft)",
          background: "var(--bg-soft, #f6f7f9)",
          padding: "6px 8px",
          borderRadius: 4,
          marginBottom: 8,
          fontStyle: "italic",
          maxHeight: 60,
          overflow: "hidden",
        }}
      >
        «{truncated}»
      </div>

      {selection.nearestLinkId && (
        <div
          className="mt-text-tight mt-muted"
          style={{
            marginBottom: 8,
            fontSize: "11px",
            textTransform: "none",
            letterSpacing: 0,
          }}
        >
          Привязано к terminology-span:{" "}
          <code>{selection.nearestLinkId}</code>
        </div>
      )}

      <div style={{ marginBottom: 8 }}>
        <div
          className="mt-text-tight mt-muted"
          style={{
            marginBottom: 4,
            fontSize: "11px",
            textTransform: "none",
            letterSpacing: 0,
          }}
        >
          Тип замечания (можно несколько):
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {(Object.keys(FEEDBACK_CATEGORY_LABELS) as FeedbackCategory[]).map(
            (cat) => {
              const active = categories.has(cat);
              return (
                <button
                  key={cat}
                  type="button"
                  className={
                    "mt-chip" + (active ? " is-active" : "")
                  }
                  style={{
                    fontSize: "11px",
                    padding: "3px 8px",
                    border: "1px solid",
                    borderColor: active
                      ? "var(--accent, #3a7bd5)"
                      : "var(--border-default, #d0d5dd)",
                    background: active
                      ? "var(--accent-bg, #eaf2fb)"
                      : "var(--panel)",
                    color: active ? "var(--accent, #3a7bd5)" : "var(--text)",
                    borderRadius: 12,
                    cursor: "pointer",
                  }}
                  onClick={() => toggleCategory(cat)}
                >
                  {FEEDBACK_CATEGORY_LABELS[cat]}
                </button>
              );
            },
          )}
        </div>
      </div>

      <textarea
        className="mt-input"
        placeholder="Комментарий (опционально)…"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        rows={3}
        style={{
          width: "100%",
          marginBottom: 8,
          fontSize: "var(--fs-sm, 13px)",
          resize: "vertical",
        }}
      />

      {lookupQuery.data && lookupQuery.data.count > 0 && (
        <div
          style={{
            marginBottom: 8,
            background: "var(--bg-soft, #f6f7f9)",
            padding: 6,
            borderRadius: 4,
            fontSize: "11px",
          }}
        >
          <div
            className="mt-text-tight mt-muted"
            style={{
              marginBottom: 4,
              fontSize: "10px",
              textTransform: "none",
              letterSpacing: 0,
            }}
          >
            Похожие термины в словаре:
          </div>
          {lookupQuery.data.items.slice(0, 3).map((c) => (
            <a
              key={c.item_id}
              href={`/admin/terminology/${encodeURIComponent(c.item_id)}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "block",
                padding: "2px 0",
                color: "var(--text)",
                textDecoration: "none",
              }}
            >
              <span className="mt-mono" style={{ fontSize: "10px" }}>
                [{c.item_type}]
              </span>{" "}
              {c.source_text} →{" "}
              <strong>{c.target_text || c.target_abbreviation}</strong>
              {c.in_draft && (
                <span
                  className="mt-badge mt-badge--info mt-badge--no-dot"
                  style={{
                    fontSize: "9px",
                    marginLeft: 6,
                    padding: "0 4px",
                  }}
                >
                  в этом переводе
                </span>
              )}
            </a>
          ))}
        </div>
      )}

      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
        <button
          type="button"
          className="mt-btn mt-btn--ghost mt-btn--sm"
          onClick={onClose}
        >
          Отмена
        </button>
        <button
          type="button"
          className="mt-btn mt-btn--primary mt-btn--sm"
          onClick={() => submit.mutate()}
          disabled={
            submit.isPending ||
            (categories.size === 0 && !comment.trim())
          }
        >
          {submit.isPending ? "Отправляю…" : "Отправить"}
        </button>
      </div>
    </div>
  );
}
